import {useState, useCallback} from 'react'

// `urgency` has no control of its own any more — the filter bar's dropdown was
// removed because sorting by Deadline already floats the most urgent to the
// top. It stays in the shape because the "Auto-accept in 48h" KPI card still
// sets it (index.js's handleExpiringClick), which is what makes that card's
// count match the rows it lands on. Keeping it here is also what surfaces
// "Clear filters": FilterBar derives that from Object.values(filters), so a
// filter the merchant cannot see still gives them a visible way out of it.
// awbs / status / sku / minExcessKg / minExcessAmount are FilterBar's "Filter" form, applied together on
// its Apply button (applyFilters) so a merchant typing or pasting does not fire a request per keystroke.
const EMPTY = {q: '', courier: '', urgency: '', awbs: '', status: '', sku: '', minExcessKg: '', minExcessAmount: ''}

/**
 * `initialRange` exists because the honest default differs by mount.
 *
 * Seeded: 'all'. Any narrower default would silently hide rows of a fixed demo dataset and nothing
 * on screen would say so until someone thought to read the date field.
 *
 * Live: '90d', because there the range IS the server's date window — 'all' sends none, and every
 * count and aggregation then reads the merchant's entire history (1,866,768 rows for the largest in
 * production). The same objection still applies, so the filter bar has to SHOW the window it is
 * using rather than leaving the control on its placeholder; 90 days covers the dispute deadline many
 * times over, and widening it is one click.
 */
export function useDisputeState({initialRange = 'all'} = {}) {
  const [tab, setTabRaw] = useState('action')
  const [filters, setFilters] = useState(EMPTY)
  const [range, setRange] = useState(initialRange)
  const [sort, setSort] = useState('urgency')
  const [page, setPage] = useState(1)
  const [selection, setSelection] = useState(() => new Set())

  const setTab = useCallback((next) => {
    setTabRaw(next)
    setPage(1)
    setSelection(new Set())
  }, [])

  const setFilter = useCallback((key, value) => {
    setFilters((f) => ({...f, [key]: value}))
    setPage(1)
  }, [])

  const resetFilters = useCallback(() => { setFilters(EMPTY); setPage(1) }, [])

  /** Several filters at once — one state change, so one request. */
  const applyFilters = useCallback((patch) => {
    setFilters((f) => ({...f, ...patch}))
    setPage(1)
  }, [])

  const toggleSelect = useCallback((id) => {
    setSelection((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }, [])

  const clearSelection = useCallback(() => setSelection(new Set()), [])

  // Replace the selection outright. The triage view's "Dispute all" needs the
  // selection to be EXACTLY one group's open rows before handing them to the
  // Evidence Studio, and clearSelection() followed by per-id toggleSelect()
  // cannot express that safely — the toggles would read a pre-clear snapshot
  // and flip ids that were already selected back off.
  const selectOnly = useCallback((ids) => setSelection(new Set(ids || [])), [])

  return {tab, setTab, filters, setFilter, applyFilters, resetFilters, range, setRange, sort, setSort, page, setPage, selection, toggleSelect, clearSelection, selectOnly}
}
